import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Select,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  // ↑ all your existing imports
} from "@chakra-ui/react";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";

export const DocumentList = () => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState("application");
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newDocument, setNewDocument] = useState({
    title: "",
    category: "application",
    file: null,
  });
  const toast = useToast();

  // ─── NEW: State for “View” modal ─────────────────────────────
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewDocument, setViewDocument] = useState({
    title: "",
    content: "",
    last_updated: "",
  });
  // ────────────────────────────────────────────────────────────

  const fetchDocuments = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL3}/admin/documents`;
      const response = await fetchWithToken(url);
      if (!response) throw new Error("Failed to fetch documents");
      setDocuments(response.documents);
      setFilteredDocuments(response.documents);
      setTotal(response.total);
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError("Failed to fetch documents");
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [category, skip, limit]);

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setFilteredDocuments(
      documents.filter((d) =>
        d.title?.toLowerCase().includes(q.toLowerCase())
      )
    );
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setSkip(0);
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setSkip(0);
  };

  const handleNextPage = () => setSkip(skip + limit);
  const handlePreviousPage = () => skip >= limit && setSkip(skip - limit);

  const handleFileChange = (e) =>
    setNewDocument({ ...newDocument, file: e.target.files[0] });

  const handleInputChange = (e) =>
    setNewDocument({ ...newDocument, [e.target.name]: e.target.value });

  const handleUploadDocument = async () => {
    const formData = new FormData();
    formData.append("file", newDocument.file);
    formData.append("title", newDocument.title);
    formData.append("category", newDocument.category);

    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL3}/admin/documents`,
        "POST",
        formData
      );
      if (!response) throw new Error("Failed to upload document");
      toast({
        title: "Success",
        description: "Document uploaded successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setIsModalOpen(false);
      fetchDocuments();
    } catch (err) {
      console.error("Error uploading document:", err);
      toast({
        title: "Error",
        description: "Failed to upload document.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL3}/documents/${documentId}`,
        { method: "DELETE" }
      );
      if (!response) throw new Error("Failed to delete document");
      toast({
        title: "Success",
        description: "Document deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchDocuments();
    } catch (err) {
      console.error("Error deleting document:", err);
      toast({
        title: "Error",
        description: "Failed to delete document.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // ─── NEW: Fetch and open “View” modal ──────────────────────────
  const handleViewDocument = async (cat) => {
    try {
      const resp = await fetchWithToken(
        `${process.env.REACT_APP_API_URL3}/admin/documents/${cat}/text`
      );
      if (!resp) throw new Error("Failed to fetch document text");
      setViewDocument({
        title: resp.title,
        content: resp.content,
        last_updated: resp.last_updated,
      });
      setIsViewModalOpen(true);
    } catch (err) {
      console.error("Error fetching document text:", err);
      toast({
        title: "Error",
        description: "Could not load document content.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };
  // ────────────────────────────────────────────────────────────

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex px="25px" justify="space-between" mb="20px" align="center">
        <Text color="gray.800" fontSize="22px" fontWeight="700">Document List</Text>
        <Flex gap="10px">
          <Button colorScheme="blue" size="sm" onClick={() => setIsModalOpen(true)}>
            Add Document
          </Button>
          <Input
            placeholder="Search documents..."
            value={searchQuery}
            onChange={handleSearch}
            size="sm"
            w="200px"
          />
          <Select
            placeholder="Filter by category"
            value={category}
            onChange={handleCategoryChange}
            size="sm"
            w="200px"
          >
            <option value="application">Application</option>
            <option value="terms_and_conditions">Terms and Conditions</option>
            <option value="companies">Companies</option>
            <option value="other">Other</option>
          </Select>
        </Flex>
      </Flex>

      <Box overflowY="auto" maxHeight="400px">
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            <Tr bg="rgb(69, 50, 240)">
              <Th textColor="white">#</Th>
              <Th textColor="white">Title</Th>
              <Th textColor="white">Category</Th>
              <Th textColor="white">Updated</Th>
              <Th textColor="white">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredDocuments.map((doc, idx) => (
              <Tr key={doc.document_id}>
                <Td>{idx + 1}</Td>
                <Td>{doc.title || "N/A"}</Td>
                <Td>{doc.category || "N/A"}</Td>
                <Td>{doc.last_updated || "N/A"}</Td>
                <Td>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => handleViewDocument(doc.category)}
                  >
                    View
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="red"
                    ml="8px"
                    onClick={() => handleDeleteDocument(doc.document_id)}
                  >
                    Delete
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* <Flex justify="space-between" px="25px" mt="10px">
        <Button size="sm" onClick={handlePreviousPage} isDisabled={skip === 0} colorScheme="blue">
          Previous
        </Button>
        <Button
          size="sm"
          onClick={handleNextPage}
          isDisabled={total === 0 || skip + limit >= total}
          colorScheme="blue"
        >
          Next
        </Button>
      </Flex> */}

      {error && (
        <Box mt="4">
          <Text color="red.500">{error}</Text>
        </Box>
      )}

      {/* ─── Add New Document Modal (unchanged) ────────────────────── */}
      {/* Modal for Adding Document */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Document</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb="4">
              <FormLabel>Title</FormLabel>
              <Input
                name="title"
                value={newDocument.title}
                onChange={handleInputChange}
                placeholder="Enter document title"
              />
            </FormControl>
            <FormControl mb="4">
              <FormLabel>Category</FormLabel>
              <Select
                name="category"
                value={newDocument.category}
                onChange={handleInputChange}
              >
                <option value="application">Application</option>
                <option value="terms_and_conditions">Terms and Conditions</option>
                <option value="companies">Companies</option>
                <option value="other">Other</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>File</FormLabel>
              <Input type="file" onChange={handleFileChange} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr="3" onClick={handleUploadDocument}>
              Upload
            </Button>
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* ──────────────────────────────────────────────────────────── */}

      {/* ─── View Document Modal ──────────────────────────────────── */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent maxH="80vh" overflowY="auto">
          <ModalHeader>{viewDocument.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* preserve line breaks: */}
            <Box whiteSpace="pre-wrap">{viewDocument.content}</Box>
          </ModalBody>
          <ModalFooter>
            <Text fontSize="sm" color="gray.500" mr="auto">
              Last updated: {new Date(viewDocument.last_updated).toLocaleString()}
            </Text>
            <Button onClick={() => setIsViewModalOpen(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* ──────────────────────────────────────────────────────────── */}
    </Card>
  );
};
